import * as Sentry from "@sentry/browser";
import React, { useEffect } from "react";
import { Navigate, useLocation, useSearchParams } from "react-router-dom";

import config from "../../../config";
import useBrowserExtensionTracking from "../../../hooks/useBrowserExtensionTracking";
import { checkCookieExistence } from "../../../utils/cookie";
import { identifyGoogleAnalytics } from "../../../utils/googleAnalytics";
import { useIntercom } from "../../../utils/intercom";
import { identifyLogrocket } from "../../../utils/logrocket";
import { useSentry } from "../../../utils/sentry";
import { SIGN_UP_ROUTES } from "../../freemium-sign-up/routes";
import { useCurrentUserQuery } from "../../graphql";
import useUpdateCurrentUser from "../../graphql/hooks/useUpdateCurrentUser";
import useAppEnvironmentContext, {
  useIsExtension,
} from "../../hooks/useAppEnvironmentContext";
import { CurrentUserContext } from "../../hooks/useCurrentUser";
import useMouseHasMoved from "../../hooks/useMouseHasMoved";

const registrationRoutes: Record<string, string> = {
  BASIC_INFO: "/register-info",
  SELECT_PERSONA: "/select-persona",
  SYNC_CALENDAR: "/sync-calendar",
  PHONE_NUMBER: "/register-phone",
  GREENHOUSE_AUTO_DRAFT: "/greenhouse-auto-draft",
  INSTALL_ZOOM_APP: "/install-zoom-app",
};

const isRegistrationRoute = (route: string): boolean => {
  return Object.values(registrationRoutes).some((path) => route.endsWith(path));
};

const isSettingsRoute = (route: string): boolean => {
  return route.indexOf("/settings") === 0 || route.indexOf("/users") === 0;
};

const getRedirectParams = (
  location: Pick<Location, "pathname" | "search">
): URLSearchParams => {
  const params = new URLSearchParams(location.search);
  if (!params.get("from")) {
    const pathname = location.pathname.replace(/^(\/interview-assistant)/, "");
    params.set("from", pathname + location.search);
  }
  return params;
};

export interface RequireAuthProps {
  children: React.ReactNode;
  hideIntercom?: boolean;
  shouldRefresh?: boolean;
  onSignInRedirect?: (params: URLSearchParams) => void;
}

// A wrapper that redirects to the login screen if you're not yet authenticated.
// Children should access currentUser with useCurrentUser().
export const RequireAuth: React.FC<RequireAuthProps> = ({
  hideIntercom = false,
  shouldRefresh = true,
  onSignInRedirect,
  children,
}) => {
  const appEnvironment = useAppEnvironmentContext();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const { data, loading } = useCurrentUserQuery({
    // pollInterval: 10 * 60 * 1000, // 10m
  });
  const currentUser = data?.currentUser;
  useBrowserExtensionTracking(useUpdateCurrentUser, currentUser);
  useIntercom(hideIntercom, currentUser);
  const isImpersonating = checkCookieExistence("is_impersonating");
  const mouseHasMoved = useMouseHasMoved();
  const isExtension = useIsExtension();
  useEffect(() => {
    if (
      !data?.currentUser ||
      isImpersonating ||
      (isExtension && !mouseHasMoved)
    )
      return;

    identifyLogrocket(data.currentUser);
    identifyGoogleAnalytics(data.currentUser);
  }, [data, isImpersonating, mouseHasMoved, isExtension]);

  useSentry(isImpersonating, currentUser?.id, currentUser?.email);

  let serverWebpackManifestHash: string | undefined | null;
  let shouldReloadOnWebpackManifestHashMismatch: string | undefined | null;
  try {
    serverWebpackManifestHash = localStorage.getItem(
      "server-webpack-manifest-hash"
    );
    shouldReloadOnWebpackManifestHashMismatch = localStorage.getItem(
      "should-reload-on-webpack-manifest-hash-mismatch"
    );
  } catch (e) {
    Sentry.captureException(e);
  }
  useEffect(() => {
    if (window.location.toString().includes("interview-assistant")) return;
    if (!shouldReloadOnWebpackManifestHashMismatch) return;
    if (!serverWebpackManifestHash) return;
    if (!config.webpackManifestHash) return;
    if (config.webpackManifestHash === "") return;
    if (serverWebpackManifestHash !== config.webpackManifestHash) {
      window.location.reload();
    }
  }, [
    serverWebpackManifestHash,
    shouldReloadOnWebpackManifestHashMismatch,
    config.webpackManifestHash,
    window.location,
  ]);

  if (loading) {
    return null;
  }

  const redirectParams = getRedirectParams(location);
  if (!currentUser) {
    const signInPath = "/sign-in";
    onSignInRedirect?.(redirectParams);
    return <Navigate to={`${signInPath}?${redirectParams.toString()}`} />;
  }

  const shouldRedirectToFreemiumSignUp =
    currentUser.organization.freemium && !currentUser.signUpCompletedAt;
  if (shouldRedirectToFreemiumSignUp) {
    onSignInRedirect?.(redirectParams);
    const continueInNewTab = ["extension", "interview-assistant"].includes(
      appEnvironment
    );
    if (continueInNewTab) {
      return <Navigate to={`/finish-sign-in?${redirectParams.toString()}`} />;
    }

    window.location.href = `${
      SIGN_UP_ROUTES.AUTH
    }?${redirectParams.toString()}`;

    return null;
  }

  const redirectPath = registrationRoutes[currentUser.signUpState];
  if (redirectPath && !location.pathname.endsWith(redirectPath)) {
    onSignInRedirect?.(redirectParams);
    return <Navigate to={`${redirectPath}?${redirectParams.toString()}`} />;
  }

  if (
    currentUser.signUpState === "COMPLETED" &&
    isRegistrationRoute(location.pathname)
  ) {
    const redirectTo = searchParams.get("from") ?? "/";
    return <Navigate to={redirectTo} />;
  }

  if (
    !currentUser.organization.interviewEnabled &&
    !isRegistrationRoute(location.pathname) &&
    !isSettingsRoute(location.pathname)
  ) {
    if (currentUser.organization.planEnabled && location.pathname === "/") {
      // user logs in and is on the home page, redirect to Plan
      window.location.href = "/plan";
      return null;
    }

    if (location.pathname !== "/upgrade") {
      // user navigates to a page that requires Plan, redirect to upgrade
      return <Navigate to="/upgrade" replace />;
    }
  }

  return (
    <CurrentUserContext.Provider value={currentUser}>
      {children}
    </CurrentUserContext.Provider>
  );
};
